import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';

/**
 * TipTap node that represents an in-article AdSense ad break.
 * Serializes to <div class="adsense-in-article-break"></div> in HTML.
 * Renders as a styled placeholder in the editor.
 */
const AdBreakNode = Node.create({
  name: 'adBreak',
  group: 'block',
  atom: true,
  draggable: true,

  parseHTML() {
    return [{ tag: 'div.adsense-in-article-break' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'adsense-in-article-break' })];
  },

  addNodeView() {
    return () => {
      const dom = document.createElement('div');
      dom.className = 'rte-ad-break-node';
      dom.contentEditable = 'false';

      const inner = document.createElement('div');
      inner.className = 'rte-ad-break-inner';
      inner.innerHTML =
        '<span class="rte-ad-break-icon">$</span>' +
        '<span class="rte-ad-break-label">In-Article Ad</span>';
      dom.appendChild(inner);

      return { dom };
    };
  },

  addCommands() {
    return {
      insertAdBreak:
        () =>
        ({ commands }) => {
          return commands.insertContent({ type: 'adBreak' });
        },
    };
  },
});

const DEFAULT_ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const DEFAULT_MAX_IMAGE_SIZE = 25 * 1024 * 1024;

function clampHtmlForEmpty(val) {
  if (!val) return '';
  const trimmed = val.trim();
  if (!trimmed || trimmed === '<p></p>' || trimmed === '<p><br></p>') return '';
  return val;
}

const ToolbarButton = ({ title, onClick, active, disabled, children }) => {
  const base =
    'inline-flex items-center justify-center h-9 w-9 rounded-md border text-sm transition-colors';
  const cls = disabled
    ? `${base} border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed`
    : active
      ? `${base} border-red-300 bg-red-50 text-red-700 hover:bg-red-100`
      : `${base} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`;

  return (
    <button type="button" title={title} onClick={onClick} className={cls} disabled={disabled}>
      {children}
    </button>
  );
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Write your post...',
  apiBaseUrl = process.env.REACT_APP_API_URL,
  allowImages = true,
}) => {
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const lastExternalValueRef = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: {
          HTMLAttributes: { class: 'rte-codeblock' },
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rte-image',
          loading: 'lazy',
        },
        allowBase64: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
      AdBreakNode,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'ProseMirror rte-editor min-h-[320px] focus:outline-none',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = clampHtmlForEmpty(ed.getHTML());
      onChange?.(html);
    },
  });

  // Keep editor in sync when parent value changes (e.g. loading an existing post)
  useEffect(() => {
    if (!editor) return;
    if (value === lastExternalValueRef.current) return;
    lastExternalValueRef.current = value;

    const current = clampHtmlForEmpty(editor.getHTML());
    const next = clampHtmlForEmpty(value);
    if (current === next) return;

    editor.commands.setContent(next || '', false);
  }, [value, editor]);

  const canEdit = !!editor && editor.isEditable;

  const promptForLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', previousUrl);
    if (url === null) return;

    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
  }, [editor]);

  const loadImages = useCallback(async () => {
    if (!apiBaseUrl) return;
    setIsLoadingImages(true);
    setImageError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/blog_get_images.php?limit=40`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to load images');
      }
      setUploadedImages(data.images || []);
    } catch (e) {
      setImageError(e.message || 'Failed to load images');
    } finally {
      setIsLoadingImages(false);
    }
  }, [apiBaseUrl]);

  const validateImageFile = useCallback((file) => {
    if (!DEFAULT_ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
    }
    if (file.size > DEFAULT_MAX_IMAGE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      throw new Error(`File too large (${sizeMB}MB). Maximum size is 25MB.`);
    }
  }, []);

  const uploadImage = useCallback(
    async (file) => {
      if (!apiBaseUrl) return null;
      validateImageFile(file);
      setUploading(true);
      setImageError(null);
      try {
        const fd = new FormData();
        fd.append('image', file);
        const resp = await axios.post(`${apiBaseUrl}/blog_upload_image.php`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        if (!resp.data?.success) {
          throw new Error(resp.data?.message || resp.data?.error || 'Upload failed');
        }
        return resp.data.image;
      } finally {
        setUploading(false);
      }
    },
    [apiBaseUrl, validateImageFile]
  );

  const insertImage = useCallback(
    (url, alt = '') => {
      if (!editor) return;
      const src = typeof url === 'string' ? url.trim() : '';
      if (!src) return false;

      const normalizedAlt = typeof alt === 'string' ? alt : '';
      let inserted = editor.chain().focus().setImage({ src, alt: normalizedAlt }).run();

      // If image insertion is rejected by the current selection context,
      // insert an explicit image node and paragraph in one transaction.
      if (!inserted) {
        inserted = editor
          .chain()
          .focus()
          .insertContent([
            { type: 'image', attrs: { src, alt: normalizedAlt } },
            { type: 'paragraph' },
          ])
          .run();
      }

      // Final fallback: HTML insertion works in edge parser scenarios.
      if (!inserted) {
        const escapedSrc = src.replace(/"/g, '&quot;');
        const escapedAlt = normalizedAlt.replace(/"/g, '&quot;');
        inserted = editor
          .chain()
          .focus()
          .insertContent(`<img src="${escapedSrc}" alt="${escapedAlt}" class="rte-image" /><p></p>`)
          .run();
      }

      if (inserted) {
        // Keep parent controlled state in sync immediately after command execution.
        const html = clampHtmlForEmpty(editor.getHTML());
        onChange?.(html);
      }

      return inserted;
    },
    [editor, onChange]
  );

  const handleInsertImageClick = useCallback(async () => {
    if (!allowImages) return;
    setShowImageBrowser(true);
    await loadImages();
  }, [allowImages, loadImages]);

  const activeAlign = useMemo(() => {
    if (!editor) return 'left';
    if (editor.isActive({ textAlign: 'center' })) return 'center';
    if (editor.isActive({ textAlign: 'right' })) return 'right';
    return 'left';
  }, [editor, editor?.state]);

  return (
    <div className="rte-root">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-t-md bg-gray-50 sticky top-0 z-10 shadow-sm">
        <ToolbarButton
          title="Bold"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          active={!!editor?.isActive('bold')}
          disabled={!canEdit || !editor?.can().chain().focus().toggleBold().run()}
        >
          <FontAwesomeIcon icon="bold" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          active={!!editor?.isActive('italic')}
          disabled={!canEdit || !editor?.can().chain().focus().toggleItalic().run()}
        >
          <FontAwesomeIcon icon="italic" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          active={!!editor?.isActive('underline')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="underline" />
        </ToolbarButton>
        <ToolbarButton
          title="Strikethrough"
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          active={!!editor?.isActive('strike')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="strikethrough" />
        </ToolbarButton>

        <div className="w-px bg-gray-200 mx-1" />

        <ToolbarButton
          title="Heading"
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          active={!!editor?.isActive('heading', { level: 2 })}
          disabled={!canEdit}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title="Bullet list"
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          active={!!editor?.isActive('bulletList')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="list-ul" />
        </ToolbarButton>
        <ToolbarButton
          title="Ordered list"
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          active={!!editor?.isActive('orderedList')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="list-ol" />
        </ToolbarButton>
        <ToolbarButton
          title="Blockquote"
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          active={!!editor?.isActive('blockquote')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="quote-right" />
        </ToolbarButton>
        <ToolbarButton
          title="Horizontal rule"
          onClick={() => editor?.chain().focus().setHorizontalRule().run()}
          active={false}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="minus" />
        </ToolbarButton>
        <ToolbarButton
          title="Code"
          onClick={() => editor?.chain().focus().toggleCode().run()}
          active={!!editor?.isActive('code')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="code" />
        </ToolbarButton>
        <ToolbarButton
          title="Code block"
          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
          active={!!editor?.isActive('codeBlock')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="file-code" />
        </ToolbarButton>

        <div className="w-px bg-gray-200 mx-1" />

        <ToolbarButton
          title="Align left"
          onClick={() => editor?.chain().focus().setTextAlign('left').run()}
          active={activeAlign === 'left'}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="align-left" />
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          onClick={() => editor?.chain().focus().setTextAlign('center').run()}
          active={activeAlign === 'center'}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="align-center" />
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          onClick={() => editor?.chain().focus().setTextAlign('right').run()}
          active={activeAlign === 'right'}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="align-right" />
        </ToolbarButton>

        <div className="w-px bg-gray-200 mx-1" />

        <ToolbarButton
          title="Link"
          onClick={promptForLink}
          active={!!editor?.isActive('link')}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon="link" />
        </ToolbarButton>

        {allowImages && (
          <ToolbarButton
            title="Insert image"
            onClick={handleInsertImageClick}
            active={false}
            disabled={!canEdit}
          >
            <FontAwesomeIcon icon="image" />
          </ToolbarButton>
        )}

        <ToolbarButton
          title="Insert in-article ad"
          onClick={() => editor?.chain().focus().insertAdBreak().run()}
          active={false}
          disabled={!canEdit}
        >
          <span className="text-xs font-bold leading-none">Ad</span>
        </ToolbarButton>

        <div className="flex-1" />

        <ToolbarButton
          title="Undo"
          onClick={() => editor?.chain().focus().undo().run()}
          active={false}
          disabled={!canEdit || !editor?.can().chain().focus().undo().run()}
        >
          <FontAwesomeIcon icon="rotate-left" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo"
          onClick={() => editor?.chain().focus().redo().run()}
          active={false}
          disabled={!canEdit || !editor?.can().chain().focus().redo().run()}
        >
          <FontAwesomeIcon icon="rotate-right" />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <div className="border border-t-0 border-gray-300 rounded-b-md bg-white p-4">
        <EditorContent editor={editor} />
      </div>

      {/* Image Browser Modal */}
      {showImageBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Insert Image</h3>
                <p className="text-sm text-gray-500">Upload to R2, then click an image to insert it into the post body.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowImageBrowser(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <FontAwesomeIcon icon="times" />
              </button>
            </div>

            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 cursor-pointer text-sm text-gray-700 w-fit">
                  <FontAwesomeIcon icon={uploading ? 'spinner' : 'cloud-arrow-up'} className={uploading ? 'animate-spin' : ''} />
                  <span>{uploading ? 'Uploading...' : 'Upload image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      try {
                        const img = await uploadImage(file);
                        if (img?.url) {
                          await loadImages();
                        }
                      } catch (err) {
                        setImageError(err?.message || 'Upload failed');
                      }
                    }}
                  />
                </label>

                <button
                  type="button"
                  onClick={loadImages}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-gray-200 hover:bg-gray-50 text-sm text-gray-700 w-fit"
                >
                  <FontAwesomeIcon icon="arrows-rotate" />
                  Refresh
                </button>

                {imageError && (
                  <div className="text-sm text-red-600 flex items-center gap-2">
                    <FontAwesomeIcon icon="circle-exclamation" />
                    <span>{imageError}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isLoadingImages ? (
                <div className="flex justify-center items-center py-12">
                  <FontAwesomeIcon icon="spinner" className="animate-spin text-2xl text-gray-400" />
                </div>
              ) : uploadedImages.length === 0 ? (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon="images" className="text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500">No images uploaded yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {uploadedImages.map((image) => (
                    <button
                      type="button"
                      key={image.id}
                      className="text-left group"
                      onClick={() => {
                        setImageError(null);
                        const didInsert = insertImage(image.url, image.alt_text || image.original_filename || '');
                        if (didInsert) {
                          setShowImageBrowser(false);
                        } else {
                          setImageError('Unable to insert this image into the editor. Please place your cursor in normal text and try again.');
                        }
                      }}
                    >
                      <div className="aspect-square overflow-hidden rounded-md border-2 border-transparent group-hover:border-red-500 transition-colors bg-gray-50">
                        <img
                          src={image.url}
                          alt={image.original_filename}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-900 truncate" title={image.original_filename}>
                          {image.original_filename}
                        </p>
                        <p className="text-xs text-gray-500">
                          {image.file_size_formatted} • {new Date(image.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;

